import { Icon, Label, token } from "@may-i/mash-design-system";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import type { Branch } from "~/entities/branch/types";
import type { BranchVersion } from "~/entities/branch-version/types";
import { useBranchVersions } from "~/features/branch-version/hooks/use-branch-versions";
import { readVersionIdForBranch } from "~/features/branch-version/lib/selected-version-storage";
import { formatValidDate } from "~/shared/lib/format";
import {
  getConfigStatusLabelKey,
  getConfigStatusVariant,
  getEffectiveStatus,
  getWorkflowPath,
} from "./utils";

// --- Table Header ---

export function TableHeader() {
  const { t } = useTranslation("branch-management");

  return (
    <HeadRow>
      <FavHeadCell />
      <HeadCell $flex>{t("table.name")}</HeadCell>
      <HeadCell $flex>{t("table.workingVersion")}</HeadCell>
      <HeadCell $width={132}>{t("table.status")}</HeadCell>
      <HeadCell $width={96}>{t("table.analysis")}</HeadCell>
      <HeadCell $width={96}>{t("table.line")}</HeadCell>
      <HeadCell $width={96}>{t("table.zone")}</HeadCell>
      <HeadCell $width={88} />
    </HeadRow>
  );
}

// --- Branch Row ---

interface BranchRowProps {
  branch: Branch;
  workingVersion: BranchVersion | undefined;
  latestVersion: BranchVersion | undefined;
  // BR-001: 버전이 0개인 매장 — 목록엔 표시하되 진입은 잠금 (레거시 canEditBranch 동작 복원)
  noVersion?: boolean;
  isPinned?: boolean;
  onTogglePin?: (uid: string) => void;
  showFavorite?: boolean;
}

export function BranchRow({
  branch,
  workingVersion,
  latestVersion,
  noVersion = false,
  isPinned,
  onTogglePin,
  showFavorite = true,
}: BranchRowProps) {
  const { t } = useTranslation("branch-management");
  const navigate = useNavigate();
  // 버전 없는 매장: 막힌 이유 툴팁을 커서를 따라 표시하기 위한 마우스 위치 (UI 는 DS Tooltip 룩과 동일)
  const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);

  // 7차 aw: stored configStatus + cameraCount/lineCount/zoneCount 결합한 effective status (PO 4 단계 기준)
  const effectiveStatus = getEffectiveStatus(branch);
  const statusVariant = getConfigStatusVariant(effectiveStatus);
  const statusLabel = t(getConfigStatusLabelKey(effectiveStatus));

  const handleConfigure = () => {
    // 버전 없는 매장은 진입 차단 — 첫 버전이 생기기 전까지 워크플로 진입 불가
    if (noVersion) return;
    const path = getWorkflowPath(effectiveStatus);
    navigate(`/branch/${branch.uid}/${path}`);
  };

  const row = (
    <BodyRow
      $disabled={noVersion}
      onClick={handleConfigure}
      onMouseMove={
        noVersion ? (e) => setTipPos({ x: e.clientX, y: e.clientY }) : undefined
      }
      onMouseLeave={noVersion ? () => setTipPos(null) : undefined}
    >
      <FavCell>
        {showFavorite && onTogglePin && (
          <FavoriteButton
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin(branch.uid);
            }}
          >
            <FavoriteIcon
              icon={isPinned ? "ic_favorite_fill" : "ic_favorite"}
              width={20}
              $active={!!isPinned}
            />
          </FavoriteButton>
        )}
      </FavCell>
      <BodyCell $flex>{branch.name}</BodyCell>
      <BodyCell $flex>
        {workingVersion ? (
          <VersionInfo>
            <DotBadge $active={workingVersion.isActive} />
            <VersionName>
              {t("version.prefix")} {workingVersion.name}
            </VersionName>
            {(workingVersion.validFrom || workingVersion.validTo) && (
              <Label variant="soft" color="default" size="xSmall">
                {workingVersion.validFrom ? formatValidDate(workingVersion.validFrom) : "-"} - {workingVersion.validTo ? formatValidDate(workingVersion.validTo) : "-"}
              </Label>
            )}
            {latestVersion && workingVersion.id === latestVersion.id && (
              <Label variant="soft" color="info" size="xSmall">
                {t("version.latest")}
              </Label>
            )}
          </VersionInfo>
        ) : (
          <NoVersionText>{t("noVersion")}</NoVersionText>
        )}
      </BodyCell>
      <BodyCell $width={132}>
        <Label variant="soft" color={statusVariant} size="small">
          {statusLabel}
        </Label>
      </BodyCell>
      <BodyCell $width={96}>
        {t("unit.camera", { count: branch.cameraCount })}
      </BodyCell>
      <BodyCell $width={96}>
        {t("unit.line", { count: branch.lineCount })}
      </BodyCell>
      <BodyCell $width={96}>
        {t("unit.zone", { count: branch.zoneCount })}
      </BodyCell>
      <ChevronCell $width={88}>
        {!noVersion && (
          <>
            <ChevronLabel>{t("configureButton")}</ChevronLabel>
            <RowChevron icon="ic_chevron_right" width={20} />
          </>
        )}
      </ChevronCell>
    </BodyRow>
  );

  // 버전 없는 매장은 진입이 막히므로, 막힌 이유를 커서를 따라다니는 툴팁으로 안내.
  // UI 는 DS TooltipContent 룩과 동일하게(아래 CursorTooltip), 동작만 커서 추적.
  if (noVersion) {
    return (
      <>
        {row}
        {tipPos &&
          createPortal(
            <CursorTooltip style={{ left: tipPos.x + 14, top: tipPos.y + 14 }}>
              {t("noVersionTooltip")}
            </CursorTooltip>,
            document.body,
          )}
      </>
    );
  }

  return row;
}

// --- Branch Row with self-fetched versions ---

interface BranchRowWithVersionsProps {
  branch: Branch;
  isPinned?: boolean;
  onTogglePin?: (uid: string) => void;
  showFavorite?: boolean;
}

export function BranchRowWithVersions({
  branch,
  isPinned,
  onTogglePin,
  showFavorite,
}: BranchRowWithVersionsProps) {
  const { data: versions } = useBranchVersions(branch.uid);
  const latestVersion = versions?.[0];
  // "작업 중인 버전" = 사용자가 이 브랜치에서 마지막으로 작업한 버전 (WorkflowLayout 이 진입 시 동기화).
  // 저장값 없으면 최신 버전 (versions[0]) 으로 fallback — WorkflowLayout 의 동일 로직과 일치.
  const rememberedId = readVersionIdForBranch(branch.uid);
  const workingVersion = versions?.find((v) => v.id === rememberedId) ?? latestVersion;
  // BR-001: versions 가 undefined 이면 아직 로딩 중 → 잠그지 않음. 로딩 끝나고 길이 0 일 때만 "버전 없음" 확정.
  const noVersion = versions !== undefined && versions.length === 0;
  return (
    <BranchRow
      branch={branch}
      workingVersion={workingVersion}
      latestVersion={latestVersion}
      noVersion={noVersion}
      isPinned={isPinned}
      onTogglePin={onTogglePin}
      showFavorite={showFavorite}
    />
  );
}

// --- Client Header Row (Admin) ---

interface ClientHeaderRowProps {
  clientName: string;
  isPinned: boolean;
  isCollapsed: boolean;
  onTogglePin: () => void;
  onToggleCollapse: () => void;
}

export function ClientHeaderRow({
  clientName,
  isPinned,
  isCollapsed,
  onTogglePin,
  onToggleCollapse,
}: ClientHeaderRowProps) {
  return (
    <ClientRow onClick={onToggleCollapse}>
      <FavCell>
        <FavoriteButton
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
        >
          <FavoriteIcon
            icon={isPinned ? "ic_favorite_fill" : "ic_favorite"}
            width={20}
            $active={isPinned}
          />
        </FavoriteButton>
      </FavCell>
      <ClientNameCell>{clientName}</ClientNameCell>
      <CollapseCell>
        <CollapseIcon
          icon={isCollapsed ? "ic_chevron_down" : "ic_chevron_up"}
          width={20}
        />
      </CollapseCell>
    </ClientRow>
  );
}

// --- Customer Client Header Row (no favorite, no collapse) ---

interface CustomerClientHeaderRowProps {
  clientName: string;
}

export function CustomerClientHeaderRow({
  clientName,
}: CustomerClientHeaderRowProps) {
  return (
    <ClientRow>
      <FavCell />
      <ClientNameCell>{clientName}</ClientNameCell>
    </ClientRow>
  );
}

// --- Styles ---

const HeadRow = styled.div`
  display: flex;
  align-items: center;
`;

const HeadCell = styled.div<{ $width?: number; $flex?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  flex: ${({ $width, $flex }) =>
    $flex ? "1 1 0%" : $width ? `0 0 ${$width}px` : "0 0 auto"};
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
`;

const FavHeadCell = styled.div`
  flex: 0 0 auto;
  padding: 0 8px;
  height: 22px;
`;

const BodyRow = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.12s ease;

  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? "transparent" : token("colors.action.hover")};
  }
`;

const BodyCell = styled.div<{ $width?: number; $flex?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 8px;
  flex: ${({ $width, $flex }) =>
    $flex ? "1 1 0%" : $width ? `0 0 ${$width}px` : "0 0 auto"};
  min-height: 46px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
`;

const FavCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 7px 8px;
  height: 46px;
`;

const FavoriteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: rgba(143, 149, 178, 0.16);
  }
`;

const FavoriteIcon = styled(Icon)<{ $active: boolean }>`
  fill: none;
  color: ${({ $active }) => ($active ? "#484BD2" : "#8F95B2")};
`;

const ClientRow = styled.div`
  display: flex;
  align-items: center;
  background-color: #edeff5;
  cursor: pointer;
`;

const ClientNameCell = styled.div`
  display: flex;
  align-items: center;
  padding: 7px 8px;
  flex: 1 1 0%;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
`;

const CollapseCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 54px;
  padding: 7px 8px;
`;

const CollapseIcon = styled(Icon)`
  color: ${token("colors.text.secondary")};
`;

const VersionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const DotBadge = styled.span<{ $active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ $active }) =>
    $active ? "#00a925" : "rgba(143, 149, 178, 0.24)"};
`;

const VersionName = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
`;

const NoVersionText = styled.span`
  font-size: 14px;
  color: ${token("colors.text.disabled")};
`;

// DS TooltipContent 와 동일한 룩 (GR900 배경·contrast 텍스트·6×8 패딩·12px·shadow) + 커서 추적용 fixed
const CursorTooltip = styled.div`
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  white-space: nowrap;
  padding: 6px 8px;
  border-radius: 4px;
  color: ${token("colors.text.contrast")};
  background-color: ${token("colors.gray.GR900")};
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  box-shadow: ${token("shadows.tooltip")};
`;

const ChevronCell = styled.div<{ $width: number }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  flex: 0 0 ${({ $width }) => $width}px;
  padding: 7px 8px;
  min-height: 46px;
  opacity: 0;
  transition: opacity 0.12s ease;

  ${BodyRow}:hover & {
    opacity: 1;
  }
`;

const ChevronLabel = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
`;

const RowChevron = styled(Icon)`
  color: ${token("colors.text.primary")};
`;
